import { Icon } from '@iconify/react';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Slider,
  ToggleButton,
  Tooltip,
  Typography
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Button } from '@nps.today/component-library';
import { Form, FormikProvider, useFormik } from 'formik';
import useSettings from 'hooks/useSettings';
import { MuiColorInput } from 'mui-color-input';
import { useState } from 'react';
import { SnowfallProps } from 'react-snowfall';
import palette from 'theme/palette';
import { useTranslator } from 'translation/useTranslator';

const SettingsChristmas = () => {
  const translator = useTranslator();
  const { enableChristmas, isDecember, onChangeEnableChristmas } = useSettings();
  const [displayChristmasSettings, setDisplayChristmasSettings] = useState(false);

  return (
    <>
      {isDecember && (
        <>
          <Wrapper>
            <Tooltip title={translator.turnOnOffJulehygge(`${!enableChristmas}`)}>
              <StyledToggleButton
                value={enableChristmas}
                selected={enableChristmas}
                onChange={(_, value: boolean) => onChangeEnableChristmas(!value)}
              >
                <SnowIcon
                  icon={`tabler:christmas-tree${enableChristmas ? '-off' : ''}`}
                  color={
                    enableChristmas ? palette.common.christmas.red : palette.common.christmas.green
                  }
                />
              </StyledToggleButton>
            </Tooltip>
            <IconButton
              id="christmasSettingsButton"
              color="primary"
              size="medium"
              onClick={() => setDisplayChristmasSettings((prevState) => !prevState)}
              sx={{ display: 'none' }}
            >
              <Icon icon="material-symbols:settings" />
            </IconButton>
          </Wrapper>
          <ChristmasSettingsDialog
            display={displayChristmasSettings}
            hideDialog={() => setDisplayChristmasSettings(false)}
          />
        </>
      )}
    </>
  );
};

export default SettingsChristmas;

const Wrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  '&:hover': {
    '#christmasSettingsButton': {
      display: 'flex'
    }
  }
}));

const StyledToggleButton = styled(ToggleButton)({
  padding: 0,
  border: 'none',
  backgroundColor: 'transparent'
});

const SnowIcon = styled(Icon)(({ theme, color }: { color: string } & any) => ({
  height: '1.5rem',
  width: '1.5rem',
  '& > *': {
    fill: color
  }
}));

type ChristmasSettingsDialogProps = {
  display: boolean;
  hideDialog: () => void;
};
const ChristmasSettingsDialog = ({ display, hideDialog }: ChristmasSettingsDialogProps) => {
  const translator = useTranslator();
  const { snowflakeValues, onChangeSnowflakeValues } = useSettings();

  const formik = useFormik<Partial<SnowfallProps>>({
    enableReinitialize: true,
    initialValues: {
      radius: snowflakeValues?.radius || [0.5, 3.0],
      wind: snowflakeValues?.wind || [-0.5, 2.0],
      snowflakeCount: snowflakeValues?.snowflakeCount || 150,
      speed: snowflakeValues?.speed || [1.0, 3.0],
      color: snowflakeValues?.color || '#dee4fd'
    },
    onSubmit: async (values) => {
      onChangeSnowflakeValues(values);
      hideDialog();
    }
  });

  const { values, submitForm, handleSubmit, getFieldProps, setFieldValue } = formik;

  const windFrom = values.wind?.[0];
  const windTo = values.wind?.[1];

  const radiusFrom = values.radius?.[0];
  const radiusTo = values.radius?.[1];

  const speedFrom = values.speed?.[0];
  const speedTo = values.speed?.[1];

  const getWindDescription = () => {
    if (windFrom === undefined || windTo === undefined) {
      return '';
    }
    const windFromDirection = windFrom > 0 ? 'west' : 'east';
    if (windFrom === windTo) {
      return translator.windFrom(`${Math.abs(windFrom)}`, windFromDirection);
    }
    const windToDirection = windTo > 0 ? 'west' : 'east';
    return translator.windFromTo(
      `${Math.abs(windFrom)}`,
      windFromDirection,
      `${Math.abs(windTo)}`,
      windToDirection
    );
  };

  const getSnowFlakeRadiusDescription = () => {
    if (radiusFrom === radiusTo) {
      return translator.snowFlakeRadius(`${radiusFrom}cm`);
    }
    return translator.snowFlakeRadiusFromTo(`${radiusFrom}`, `${radiusTo}`);
  };

  return (
    <Dialog open={display} onClose={hideDialog} style={{ overflow: 'display' }}>
      <DialogTitle>{translator.snowSettings()}</DialogTitle>
      <DialogContent style={{ paddingTop: '2rem' }}>
        <Box style={{ width: '20rem', maxWidth: '100%' }}>
          <FormikProvider value={formik}>
            <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
              <FormControlLabel
                control={
                  <Slider
                    style={{ overflow: 'display' }}
                    {...getFieldProps('wind')}
                    valueLabelDisplay="auto"
                    min={-5}
                    max={5}
                  />
                }
                label={translator.wind()}
                labelPlacement="start"
                sx={{ mx: 0, width: '100%', justifyContent: 'space-between', gap: '.5rem' }}
              />
              {typeof windFrom === 'number' && typeof windTo === 'number' && (
                <Description>{getWindDescription()}</Description>
              )}

              <FormControlLabel
                control={
                  <Slider
                    style={{ overflow: 'display' }}
                    {...getFieldProps('radius')}
                    valueLabelDisplay="auto"
                    min={1}
                    max={20}
                  />
                }
                label={translator.size()}
                labelPlacement="start"
                sx={{ mx: 0, width: '100%', justifyContent: 'space-between', gap: '.5rem' }}
              />
              {radiusFrom && radiusTo && (
                <Description>{getSnowFlakeRadiusDescription()}</Description>
              )}

              <FormControlLabel
                control={
                  <Slider
                    style={{ overflow: 'display' }}
                    {...getFieldProps('snowflakeCount')}
                    valueLabelDisplay="auto"
                    min={1}
                    max={1000}
                  />
                }
                label={translator.amount()}
                labelPlacement="start"
                sx={{ mx: 0, width: '100%', justifyContent: 'space-between', gap: '.5rem' }}
              />
              {radiusFrom && radiusTo && (
                <Description>
                  {translator.snowFlakeCount(`${values.snowflakeCount ?? 0}`)}
                </Description>
              )}

              <FormControlLabel
                control={
                  <Slider
                    style={{ overflow: 'display' }}
                    {...getFieldProps('speed')}
                    valueLabelDisplay="auto"
                    min={1}
                    max={20}
                  />
                }
                label={translator.gravity()}
                labelPlacement="start"
                sx={{ mx: 0, width: '100%', justifyContent: 'space-between', gap: '.5rem' }}
              />
              {speedFrom && speedTo && (
                <>
                  <Description>{`${speedFrom}G${
                    (speedFrom !== speedTo && ` to ${speedTo}G`) || ''
                  }`}</Description>
                  <Description>{translator.speedFromToG(`${speedFrom}`, `${speedTo}`)}</Description>
                </>
              )}

              <FormControlLabel
                control={
                  <MuiColorInput
                    format="hex"
                    value={getFieldProps('color').value}
                    onChange={(color) => {
                      setFieldValue('color', '#' + color);
                    }}
                  />
                }
                label={translator.color()}
                labelPlacement="start"
                sx={{ mx: 0, width: '100%', justifyContent: 'space-between', gap: '.5rem' }}
              />
            </Form>
          </FormikProvider>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          dataTestId="christmas-dialog-close"
          variant="outlined"
          color="primary"
          onClick={hideDialog}
        >
          {translator.close()}
        </Button>
        <Button
          dataTestId="christmas-dialog-save"
          type="submit"
          variant="contained"
          onClick={submitForm}
        >
          {translator.save()}
        </Button>
      </DialogActions>
      <SantaIcon icon="emojione-v1:santa-claus" width="7rem" />
    </Dialog>
  );
};

const Description = styled(Typography)({
  fontWeight: 400,
  fontSize: '.75rem',
  paddingBottom: '1rem'
});

const SantaIcon = styled(Icon)({
  position: 'absolute',
  bottom: 0,
  opacity: '.5',
  pointerEvents: 'none'
});
